import { useState, type CSSProperties } from "react";
import { RotateCcw, X } from "lucide-react";
import type { CropArea, PreparedImage } from "../lib/types";
import { normalizeCropArea } from "../lib/image";

const FULL_CROP: CropArea = { x: 0, y: 0, width: 1, height: 1 };

interface CropEditorProps {
  title: string;
  image: PreparedImage;
  initialCrop?: CropArea;
  onCancel: () => void;
  onApply: (cropArea: CropArea) => void;
}

export function CropEditor({ title, image, initialCrop, onCancel, onApply }: CropEditorProps) {
  const [crop, setCrop] = useStateSafeCrop(initialCrop ?? FULL_CROP);
  const cropStyle: CSSProperties = {
    left: `${crop.x * 100}%`,
    top: `${crop.y * 100}%`,
    width: `${crop.width * 100}%`,
    height: `${crop.height * 100}%`
  };

  function updateCrop(next: Partial<CropArea>) {
    setCrop((current) => normalizeCropArea({ ...current, ...next }));
  }

  function setPreset(aspect: number) {
    const width = aspect >= 1 ? 1 : aspect;
    const height = aspect >= 1 ? 1 / aspect : 1;
    setCrop(
      normalizeCropArea({
        x: (1 - width) / 2,
        y: (1 - height) / 2,
        width,
        height
      })
    );
  }

  return (
    <div className="modal-backdrop" role="presentation">
      <section className="crop-dialog" role="dialog" aria-modal="true" aria-label={`${title}裁剪`}>
        <header className="modal-heading">
          <div>
            <h2>{title}裁剪</h2>
            <p>
              {image.width} x {image.height}
            </p>
          </div>
          <button className="icon-button" type="button" onClick={onCancel} aria-label="关闭裁剪" title="关闭">
            <X size={18} />
          </button>
        </header>

        <div className="crop-workspace">
          <div className="crop-stage">
            <div className="crop-image-wrap">
              <img src={image.dataUri} alt={`${title}裁剪预览`} draggable={false} />
              <div className="crop-frame" style={cropStyle} aria-hidden="true">
                <span />
                <span />
                <span />
                <span />
              </div>
            </div>
          </div>

          <div className="crop-controls">
            <div className="preset-row" role="group" aria-label="裁剪比例">
              <button type="button" onClick={() => setCrop(FULL_CROP)}>
                全图
              </button>
              <button type="button" onClick={() => setPreset(1)}>
                方图
              </button>
              <button type="button" onClick={() => setPreset(4 / 3)}>
                横图
              </button>
              <button type="button" onClick={() => setPreset(3 / 4)}>
                竖图
              </button>
            </div>

            <CropSlider label="左侧" value={crop.x} max={1 - crop.width} onChange={(x) => updateCrop({ x })} />
            <CropSlider label="顶部" value={crop.y} max={1 - crop.height} onChange={(y) => updateCrop({ y })} />
            <CropSlider label="宽度" value={crop.width} min={0.08} max={1 - crop.x} onChange={(width) => updateCrop({ width })} />
            <CropSlider label="高度" value={crop.height} min={0.08} max={1 - crop.y} onChange={(height) => updateCrop({ height })} />

            <div className="modal-actions">
              <button className="text-button" type="button" onClick={() => setCrop(FULL_CROP)}>
                <RotateCcw size={16} />
                重置
              </button>
              <button className="primary-action compact" type="button" onClick={() => onApply(crop)}>
                应用裁剪
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

interface CropSliderProps {
  label: string;
  value: number;
  min?: number;
  max: number;
  onChange: (value: number) => void;
}

function CropSlider({ label, value, min = 0, max, onChange }: CropSliderProps) {
  const boundedMax = Math.max(min, max);
  const displayValue = Math.round(value * 100);

  return (
    <label className="crop-slider">
      <span>
        {label}
        <strong>{displayValue}%</strong>
      </span>
      <input
        type="range"
        min={Math.round(min * 100)}
        max={Math.round(boundedMax * 100)}
        value={Math.round(Math.min(value, boundedMax) * 100)}
        onChange={(event) => onChange(Number(event.currentTarget.value) / 100)}
      />
    </label>
  );
}

function useStateSafeCrop(initial: CropArea) {
  return useState(() => normalizeCropArea(initial));
}
